import { AuthContainer, JWT_NAME_ACCESS } from '@sleepstation/auth-jwt'
import { ErrorBoundary } from '@sleepstation/components'
import { ErrorReporterContext } from '@sleepstation/components-context'
import { replaceTokens } from '@sleepstation/utils'
import { any, curry, forEach, lensPath, over } from 'ramda'
import { isNotEmpty } from 'ramda-adjunct'
import React, { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { useLoaderData } from 'react-router'
import { useContainer } from 'unstated-next'
import { useQuery } from 'urql'
import LocalStorageContext from '../../../context/LocalStorageContext'
import RoutingContext from '../../../context/RoutingContext'
import {
  CurrentSleepReportQuery,
  MarkReportSeenMutation,
} from '../../../graphql/documents'
import useDisplayError from '../../../hooks/useDisplayError'
import PageNavContainer from '../../../state/PageNavContainer'
import SleepReportErrorNotification from '../../errorNotifications/SleepReportErrorNotification'
import LoadOverlay from '../../shared/LoadOverlay'
import SleepReport from './SleepReport/SleepReport'
import useMarkResult from './SleepReport/hooks/useMarkResult'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const PAGE_ID = `currentSleepReport`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const isErrorOfType = curry(
  (type, error) => error.type === type || error?.extensions.type === type
)

const containsErrorOfType = (type, errors) => any(isErrorOfType(type), errors)

const handleError = (
  displayError,
  errorReporter,
  localStorageApi,
  authFailHandler,
  { graphQLErrors, networkError }
) => {
  if (isNotEmpty(graphQLErrors)) {
    forEach(
      (error) =>
        errorReporter.send(error, {
          reporterCalledFrom: `SleepReportModule`,
        }),
      graphQLErrors
    )

    if (containsErrorOfType(`TokenSignatureExpiredError`, graphQLErrors)) {
      localStorageApi.clearValue(JWT_NAME_ACCESS)
      authFailHandler()
      return null
    }
  } else {
    errorReporter.send(networkError, {
      reporterCalledFrom: `SleepReportModule`,
    })
  }

  const error = networkError || graphQLErrors[0]

  displayError(`There was a problem accessing this resource`, error, {
    isFatal: true,
    buttonLabel: `Refresh page`,
  })
  return null
}

const overPagesCurrentSleepReportResultsSectionSecondaryCtasDownloadBody = over(
  lensPath([
    `page`,
    `content`,
    `resultsSection`,
    `secondaryCtas`,
    `questions`,
    `body`,
  ])
)

const injectRouting = (routing, content) =>
  overPagesCurrentSleepReportResultsSectionSecondaryCtasDownloadBody(
    (template) =>
      replaceTokens(template, {
        appChatMessagesRoute: routing.chatMessagesPath,
      })
  )(content)

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const SleepReportModule = () => {
  const loaderData = useLoaderData()
  const { displayError } = useDisplayError()
  const localStorageApi = useContext(LocalStorageContext)
  const errorReporter = useContext(ErrorReporterContext)
  const { appRouting } = useContext(RoutingContext)
  const loaderDataWithRouting = injectRouting(appRouting, loaderData)

  // Data
  // ---------------------------------------------------------------------------

  const [isErrored, setIsErrored] = useState(false)

  const [, setNav] = PageNavContainer.useContainer()

  const { authFailHandler } = useContainer(AuthContainer)

  const [result] = useQuery({
    query: CurrentSleepReportQuery,
    variables: {},
  })

  const { data, error } = result

  // Note: We don't do anything differently if this fails, other than log the
  // error to our reporter
  useMarkResult(MarkReportSeenMutation, result.data, errorReporter)

  // Effect
  // ---------------------------------------------------------------------------

  useLayoutEffect(() => {
    const sharedNav = {
      pageId: PAGE_ID,
    }

    if (isErrored) {
      setNav(sharedNav)
    } else {
      setNav({
        ...sharedNav,
        headerIsFixed: true,
        backgroundIsHidden: true,
      })
    }
  }, [data, setNav, isErrored])

  useEffect(() => {
    if (error) {
      handleError(
        displayError,
        errorReporter,
        localStorageApi,
        authFailHandler,
        error
      )
    }
  }, [error, authFailHandler, displayError, errorReporter, localStorageApi])

  return !data || error ? (
    <LoadOverlay />
  ) : (
    <ErrorBoundary
      exceptionName="sleep-report"
      Fallback={SleepReportErrorNotification}
      onError={() => {
        setIsErrored(true)
      }}
    >
      <SleepReport
        currentSleepReport={data.currentSleepReport}
        content={loaderDataWithRouting.page.content}
      />
    </ErrorBoundary>
  )
}

export default SleepReportModule
